<script setup>
import { onBeforeMount, onMounted, ref, useTemplateRef, watchEffect } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import { getWorkflows } from '@/services/workflowService.js'
import { getCampaign, getCampaignTemplates, getCampaignTypes } from '@/services/campaignService.js'
import { sanitizeInputAmount } from '@/libs/utils.js'

import moment from 'moment/moment'
import Paginazione from '@/components/Generics/Paginations/Paginazione.vue'
import ButtonTornaAllaLista from '@/components/Generics/Buttons/ButtonTornaAllaLista.vue'

import { client } from '@/services/fetch.js'
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome'
import { faTrash } from '@fortawesome/free-solid-svg-icons'
import { toast } from '@/services/toast.js'
import { library } from '@fortawesome/fontawesome-svg-core'
import { useSummaryCampaign } from '@/services/store.js'
import TranslationProvider from '@/provider/translations.js'

library.add(faTrash)

const props = defineProps(['updatePageTitle'])
const router = useRouter()
const route = useRoute()
const cId = route.params.id

const loadingForm = ref(false)
const template = ref([])
const selectedTemplate = ref({})
const selectedWorkflow = ref(null)
const selectedFixed = ref(false)
const hideFields = ref(true)
const networkError = ref({
  enabled: false,
  message: 'Errore nel salvataggio dei dati , riprova più tardi'
})
const titleError = ref({
  enabled: false,
  message: TranslationProvider.getTrans('app.mandatory_field')
})
const dateStartError = ref({
  enabled: false,
  message: TranslationProvider.getTrans('app.mandatory_field')
})
const dateEndError = ref({
  enabled: false,
  message: 'La data di fine deve essere maggiore della data odierna'
})
const workflow = ref([])
const attributes = ref()
const newAttributeKey = ref('')
const newAttributeValue = ref('')
const title = ref('')
const types = ref([])
let selectedType = defineModel('selectedType')
const dateFrom = defineModel('date-from')
const dateTo = defineModel('date-to')
const dateStart = defineModel('dateStart')
const dateEnd = defineModel('dateEnd')
const amount = defineModel('amount')
const loadingGec = ref(false)
const errorGec = ref({
  enabled: false,
  message: 'Errore in fase di ricerca su Contract Management'
})
const gecContracts = ref(null)
const noGecContractsFound = ref(false)
const modalCreateCampaign = useTemplateRef('modal-create-campaign')
const card = useTemplateRef('card')
const creatingCampaignAndCompiledPolls = ref(false)
const creatingCampaignAndCompiledPollsNums = ref({
  created: 0,
  skipped: 0,
  errors: 0
})
const creatingCampaignAndCompiledPollsError = ref({
  enabled: false,
  message: 'Errore creazione campagna e questionari'
})

const creatingCampaignAndCompiledPollsPerc = ref(0)
const campaign = ref(null)
const summaryCampaign = useSummaryCampaign()
const canView = ref(false)

onBeforeMount(async () => {
  canView.value = route.meta?.permissions?.canView ?? false

  if (!canView.value) {
    return
  }

  loadingForm.value = true

  const campaignTemplates = await getCampaignTemplates()
  template.value = campaignTemplates.data

  workflow.value = await getWorkflows('campaign')

  types.value = await getCampaignTypes()
  selectedType.value = types.value[0].key

  loadingForm.value = false
})

onMounted(() => {
  const now = moment()
  dateFrom.value = moment(now)
    .subtract(1, 'year')
    .month(0)
    .date(1)
    .format('YYYY-MM-DD')
  dateTo.value = moment(now)
    .subtract(1, 'year')
    .month(11)
    .date(31)
    .format('YYYY-MM-DD')

  window.onbeforeunload = function() {
    if (creatingCampaignAndCompiledPolls.value) {
      return ''
    } else {
      return null
    }
  }
})

function showPicker(e) {
  e.target.showPicker()
}

const addAttribute = () => {
  if (!attributes.value) {
    attributes.value = {}
  }

  if (newAttributeKey.value && newAttributeValue.value) {
    attributes.value = {
      ...attributes.value,
      [newAttributeKey.value]: newAttributeValue.value
    }
    newAttributeKey.value = ''
    newAttributeValue.value = ''
  }
}

const removeAttribute = key => {
  delete attributes.value[key]
}

const loadContracts = async (page = 1, refreshList = true) => {
  if (!validateCampaign()) {
    return
  }

  noGecContractsFound.value = false
  errorGec.value.enabled = false
  if (refreshList) {
    loadingGec.value = true
  }
  const url = '/custom/cmb/gec'
  const startDate = dateFrom.value
  const endDate = dateTo.value
  const amountV = amount.value.toString().replace(',', '.')
  try {
    const response = await client.get(
      url +
      `?page=${page}&limit=50&startDate=${startDate}&endDate=${endDate}&amount=${amountV}`
    )
    if (response['getContractCollection']) {
      if (refreshList) {
        gecContracts.value = response['getContractCollection']
        if (gecContracts.value.length <= 0) {
          noGecContractsFound.value = true
          toast.create('Nessun contratto trovato', 'warning')
        }
      } else {
        return response['getContractCollection']
      }
    } else {
      if (refreshList) {
        noGecContractsFound.value = true
        toast.create('Nessun contratto trovato', 'warning')
      }
    }
  } catch (error) {
    // errorGec.value.enabled = true
    toast.create('Errore in fase di ricerca su Contract Management', 'error')
    window.scrollTo(0, 0)
  }
  if (refreshList) {
    loadingGec.value = false
  }
}

const getSubject = (contract, role) => {
  const is = contract['involvedSubjects']
  for (const subject of is) {
    if (subject['role'] === role) {
      return subject['firstName'] + ' ' + subject['lastName']
    }
  }
  return ''
}

const handleCreateCampaign = async confirm => {
  if (!validateCampaign()) {
    window.scrollTo(0, 0)
    return
  }
  creatingCampaignAndCompiledPollsError.value.message =
    'Errore creazione campagna'
  creatingCampaignAndCompiledPollsError.value.enabled = false
  if (!confirm) {
    modalCreateCampaign.value.classList.add('d-block')
  } else {
    creatingCampaignAndCompiledPolls.value = true
    try {
      if (dateFrom.value) {
        attributes.value = {
          ...attributes.value,
          ['startDate']: dateFrom.value
        }
      }
      if (dateTo.value) {
        attributes.value = {
          ...attributes.value,
          ['endDate']: dateTo.value
        }
      }
      if (amount.value) {
        attributes.value = {
          ...attributes.value,
          ['amount']: amount.value
        }
      }

      let url = '/campaign/api/campaign/'
      if (cId) {
        url = `/campaign/api/campaign/${cId}`
      }
      const campaign = {
        pollTemplateReference: selectedTemplate.value.reference,
        pollTemplateRevisionNumber: selectedTemplate.value.revisionNumber,
        fixed: selectedFixed.value,
        attributes:
          attributes.value && Object.keys(attributes.value).length
            ? attributes.value
            : null,
        workflow: selectedWorkflow.value,
        title: title.value,
        type: selectedType.value,
        dateStart: dateStart.value,
        dateEnd: dateEnd.value ? dateEnd.value : null
      }

      let res
      if (!cId) {
        res = await client.post(url, campaign, 'application/json')
      } else {
        res = await client.put(url, campaign, 'application/json')
      }
      if (res && res.id) {
        await createCompiledPolls(res)

        summaryCampaign.setSummary(res.id, creatingCampaignAndCompiledPollsNums.value)

        if (!creatingCampaignAndCompiledPollsError.value.enabled) {
          toast.create('Campagna e relativi questionari creati', 'success')

          await router.push({
            name: 'admin/CMB_CampaignSummary',
            query: { page: route.query.page },
            params: { cId: res.id }
          })
        }
      } else {
        console.error(res)
        creatingCampaignAndCompiledPollsError.value.enabled = true
        creatingCampaignAndCompiledPollsError.value.message =
          'Errore creazione questionari'
        window.scrollTo(0, 0)
        modalCreateCampaign.value.classList.remove('d-block')
      }
    } catch (error) {
      toast.create('Errore salvataggio campagna', 'error')
      window.scrollTo(0, 0)
      modalCreateCampaign.value.classList.remove('d-block')
    }
    creatingCampaignAndCompiledPolls.value = false
  }
}

const updateCampaign = async () => {
  if (!validateCampaign()) {
    window.scrollTo(0, 0)
    return
  }
  creatingCampaignAndCompiledPolls.value = true
  creatingCampaignAndCompiledPollsError.value.enabled = false
  creatingCampaignAndCompiledPollsError.value.message =
    'Errore creazione campagna'
  try {
    let url = `/campaign/api/campaign/${cId}`
    const payload = {
      pollTemplateReference: selectedTemplate.value.reference,
      pollTemplateRevisionNumber: selectedTemplate.value.revisionNumber,
      fixed: selectedFixed.value,
      attributes:
        attributes.value && Object.keys(attributes.value).length
          ? attributes.value
          : null,
      workflow: selectedWorkflow.value,
      title: title.value,
      type: selectedType.value,
      dateStart: dateStart.value,
      dateEnd: dateEnd.value ? dateEnd.value : null
    }

    await client.put(url, payload)
    toast.create('Campagna salvata con successo')
    window.scrollTo(0, 0)
  } catch (e) {
    console.error(e)
    toast.create('Errore salvataggio campagna', 'error')
    window.scrollTo(0, 0)
  }

  creatingCampaignAndCompiledPolls.value = false
}

const createCompiledPolls = async (campaign, page = 1, created = [], errors = []) => {
  const gecContracts = await loadContracts(page, false)
  if (gecContracts && page <= gecContracts['lastPage']) {
    let payload = {
      campaign: {
        id: campaign.id,
        pollTemplateRevisionNumber: campaign.pollTemplateRevisionNumber,
        permissions: campaign.permissions
      },
      new: false,
      contracts: []
    }
    for (const gecContract of gecContracts['items']) {
      payload.contracts.push(gecContract)
    }
    try {
      const url = '/custom/cmb/create-compiled-polls'
      const res = await client.post(url, payload, 'application/json')
      if (res && res['compiledPolls']) {
        created = created.concat(res['compiledPolls']['data'])
        errors = errors.concat(res['compiledPolls']['errors'])
        // skippedContracts = skippedContracts.concat(res['skippedContracts'])
        let numWithErrors = payload.contracts.length - (res['compiledPolls']['data'].length + res['skippedContracts'].length)
        creatingCampaignAndCompiledPollsNums.value.created += res['compiledPolls']['data'].length
        creatingCampaignAndCompiledPollsNums.value.skipped += res['skippedContracts'].length
        creatingCampaignAndCompiledPollsNums.value.errors += numWithErrors
      }
      if (parseInt(gecContracts['total'])) {
        creatingCampaignAndCompiledPollsPerc.value = Math.ceil(
          (parseInt(gecContracts['count']) *
            parseInt(gecContracts['currentPage']) *
            100) /
          parseInt(gecContracts['total']))
      } else {
        creatingCampaignAndCompiledPollsPerc.value = 100
      }
    } catch (error) {
      creatingCampaignAndCompiledPollsError.value.enabled = true
      creatingCampaignAndCompiledPollsError.value.message =
        `Errore creazione questionari: pagina ${page}`
    }

    // COMMENTARE SOLO PER TEST
    created = await createCompiledPolls(campaign, page + 1, created, errors)
  }

  return created
}

const detailCampaign = async campaignId => {
  try {
    campaign.value = await getCampaign(campaignId, {poll: 1})

    selectedTemplate.value = {
      reference: campaign.value.pollTemplateReference,
      revisionNumber: campaign.value.pollTemplateRevisionNumber,
      title: campaign.value?.poll.title
    }

    selectedWorkflow.value = campaign.value.workflow
    selectedFixed.value = campaign.value.fixed
    title.value = campaign.value.title
    dateStart.value = campaign.value.dateStart ? campaign.value.dateStart.split('T')[0] : null
    dateEnd.value = campaign.value.dateEnd ? campaign.value.dateEnd.split('T')[0] : null

    if (!campaign.value.attributes) {
      campaign.value.attributes = {}
    } else {
      attributes.value = campaign.value.attributes
      if (campaign.value.attributes['startDate']) {
        dateFrom.value = campaign.value.attributes['startDate']
      }
      if (campaign.value.attributes['endDate']) {
        dateTo.value = campaign.value.attributes['endDate']
      }
      if (campaign.value.attributes['amount']) {
        amount.value = campaign.value.attributes['amount']
      }
    }
  } catch (error) {
    window.scrollTo(0, 0)
  }
}

const handleAmount = () => {
  amount.value = (Math.round(amount.value * 100) / 100).toFixed(2)
}

const validateCampaign = () => {
  titleError.value.enabled = false
  dateStartError.value.enabled = false
  dateEndError.value.enabled = false
  dateEndError.value.message = TranslationProvider.getTrans('app.mandatory_field')
  dateStartError.value.message = TranslationProvider.getTrans('app.mandatory_field')
  if (!title.value || title.value.trim() === '') {
    titleError.value.enabled = true
  }
  if (!dateStart.value || dateStart.value.trim() === '') {
    dateStartError.value.enabled = true
  } else {
    const dmyS = dateStart.value.split('-')
    const dateStartD = new Date(dmyS[0], dmyS[1] - 1, dmyS[2])
    const now = new Date()
    now.setHours(0, 0, 0, 0)

    if ((campaign.value == null || campaign.value.state === 'inactive') && dateStartD.getTime() < now.getTime()) {
      dateStartError.value.enabled = true
      dateStartError.value.message = 'La data di inizio non può essere antecedente a quella odierna'
    }
  }
  if (dateEnd.value) {
    const dmyE = dateEnd.value.split('-')
    const dateEndD = new Date(dmyE[0], dmyE[1] - 1, dmyE[2])
    const now = new Date()
    now.setHours(0, 0, 0, 0)

    if ((campaign.value == null || campaign.value.state !== 'closed') && dateEndD.getTime() <= now.getTime()) {
      dateEndError.value.enabled = true
      dateEndError.value.message = 'La data di fine deve essere dopo quella odierna'
    }

    const dmyS = dateStart.value.split('-')
    const dateStartD = new Date(dmyS[0], dmyS[1] - 1, dmyS[2])
    if ((campaign.value == null || campaign.value.state !== 'closed') && dateEndD.getTime() < dateStartD.getTime()) {
      dateEndError.value.enabled = true
      dateEndError.value.message = 'La data di fine non può essere antecedente a quella di inizio'
    }
  }

  return !(titleError.value.enabled || dateStartError.value.enabled || dateEndError.value.enabled)
}

watchEffect(async () => {
  if (cId) {
    await detailCampaign(cId)
    let pageTitle = 'Modifica campagna - ' + campaign.value.title
    props.updatePageTitle(pageTitle)
  }
})
</script>

<template>
  <div class="container-fluid px-0">
    <div class="card" ref="card">
      <div class="card-body" v-if="loadingForm">
        <div class="d-flex justify-content-center">
          <div class="spinner-grow" role="status">
            <span class="sr-only">Loading...</span>
          </div>
        </div>
      </div>
      <div class="card-body scrollable-body m-0" v-else ref="card-body">
        <div
          v-if="networkError.enabled"
          class="alert alert-danger mt-2"
          role="alert"
        >
          {{ networkError.message }}
        </div>

        <div
          v-if="noGecContractsFound"
          class="alert alert-warning mt-2"
          role="alert"
        >
          Nessun contratto trovato
        </div>

        <div class="mb-3 col-12 px-0">
          <label for="title">Titolo *</label>
          <input
            type="text"
            class="form-control"
            v-model="title"
            placeholder="Titolo"
            :class="{'is-invalid': titleError.enabled && titleError.message }"
            required
          />
          <div class="text-danger" v-if="titleError.enabled && titleError.message" v-html="titleError.message"></div>
        </div>

        <div class="mb-3">
          <label for="template">Template *</label>
          <v-select
            :disabled="cId !== null"
            :options="template"
            label="title"
            :reduce="
              temp => ({
                reference: temp.reference,
                revisionNumber: temp.revisionNumber
              })
            "
            v-model="selectedTemplate"
            placeholder="Seleziona un template"
          >
            <template #no-options>
              <div style="opacity: 0.8">{{ TranslationProvider.getTrans('app.no_result_found') }}</div>
            </template>
          </v-select>
        </div>
        <div class="mb-3">
          <label for="workflow">Workflow *</label>
          <v-select
            :disabled="cId !== null"
            v-model="selectedWorkflow"
            :options="workflow"
            :reduce="perm => perm.key"
            required
            placeholder="Seleziona un workflow"
          >
            <template #no-options>
              <div style="opacity: 0.8">{{ TranslationProvider.getTrans('app.no_result_found') }}</div>
            </template>
          </v-select>
        </div>
        <div class="container-fluid mb-2 px-0">
          <div class="row">
            <div class="col-12 col-lg-6">
              <label for="dateStart">Data inizio campagna *</label>
              <input
                id="dateStart"
                type="date"
                class="form-control"
                v-model="dateStart"
                @click="showPicker"
                :readonly="!(cId == null || campaign?.state === 'inactive')"
                :disabled="!(cId == null || campaign?.state === 'inactive')"
                placeholder="Data dal"
                :class="{'is-invalid': dateStartError.enabled && dateStartError.message }"
              />
              <div class="text-danger" v-if="dateStartError.enabled && dateStartError.message" v-html="dateStartError.message"></div>
            </div>
            <div class="col-12 col-lg-6">
              <label for="dateEnd">Data fine campagna</label>
              <input
                id="dateEnd"
                type="date"
                class="form-control"
                v-model="dateEnd"
                @click="showPicker"
                :readonly="cId !== null && campaign?.state === 'closed'"
                :disabled="cId !== null && campaign?.state === 'closed'"
                :class="{'is-invalid': dateEndError.enabled && dateEndError.message }"
                placeholder="Data al"
              />
              <div class="text-danger" v-if="dateEndError.enabled && dateEndError.message" v-html="dateEndError.message"></div>
            </div>
          </div>
        </div>
        <div class="mb-3 container-fluid px-0">
          <div class="row">
            <div class="col-12 pt-3">
              <h6>{{ TranslationProvider.getTrans('app.campaign_form.contract_dates') }}</h6>
            </div>
            <div class="col-12 col-lg-4">
              <label for="dateFrom">Data dal *</label>
              <input
                :disabled="cId !== null"
                id="dateFrom"
                type="date"
                class="form-control"
                v-model="dateFrom"
                @click="showPicker"
              />
            </div>
            <div class="col-12 col-lg-4 mt-3 mt-lg-0">
              <label for="dateFrom">Data al *</label>
              <input
                :disabled="cId !== null"
                id="dateTo"
                type="date"
                class="form-control"
                v-model="dateTo"
                @click="showPicker"
              />
            </div>
            <div class="col-12 col-lg-4 mt-3 mt-lg-0">
              <label for="dateFrom">Importo maggiore o uguale a *</label>
              <input
                :disabled="cId !== null"
                id="dateTo"
                type="text"
                class="form-control"
                v-model="amount"
                placeholder="Importo maggiore o uguale a"
                inputmode="decimal"
                pattern="[0-9]*[.,]?[0-9]*"
                @input="amount = sanitizeInputAmount(amount)"
              />
            </div>
          </div>
        </div>
        <div class="mb-3" v-if="!hideFields">
          <label for="">Attributi</label>
          <div v-for="(value, key) in attributes" :key="key" class="mb-2">
            <div class="input-group">
              <span class="input-group-text">{{ key }}</span>
              <input
                type="text"
                class="form-control"
                v-model="attributes[key]"
                placeholder="Modifica valore"
              />
              <button class="btn btn-danger" @click="removeAttribute(key)">
                <font-awesome-icon icon=" fa-trash" />
              </button>
            </div>
          </div>

          <div class="input-group mt-3">
            <input
              type="text"
              class="form-control"
              v-model="newAttributeKey"
              placeholder="Nuovo attributo"
            />
            <input
              type="text"
              class="form-control"
              v-model="newAttributeValue"
              placeholder="Valore"
            />
            <button class="btn btn-secondary" @click="addAttribute">
              Aggiungi Attributo
            </button>
          </div>
        </div>

        <div class="row">
          <div class="mb-3 col-12 col-lg-6 ps-lg-2" v-if="!hideFields">
            <label for="type">Tipologia</label>
            <v-select
              v-model="selectedType"
              :options="types"
              :reduce="type => type.key"
              placeholder="Seleziona una Tipologia"
              label="label"
            >
              <template #no-options>
                <div style="opacity: 0.8">{{ TranslationProvider.getTrans('app.no_result_found') }}</div>
              </template>
            </v-select>
          </div>
        </div>

        <div class="form-check mb-3" v-if="!hideFields">
          <input
            class="form-check-input"
            type="checkbox"
            id="flexCheckDefault"
            v-model="selectedFixed"
          />
          <label class="form-check-label" for="fixed"> Fissa </label>
        </div>

        <div
          class="table-responsive border-top py-3"
          v-if="gecContracts !== null && !loadingGec"
        >
          <p>
            Sono presenti <b>{{ gecContracts.total }}</b> contratti. <span
            v-if="campaign && campaign.compiledPollNumber">Al momento sono presenti <b>{{ campaign.compiledPollNumber
            }}</b> questionari.</span>
          </p>
          <table class="table">
            <thead>
            <tr>
              <th>{{ TranslationProvider.getTrans('app.codice_contratto') }}</th>
              <th>{{ TranslationProvider.getTrans('app.oggetto') }}</th>
              <th>{{ TranslationProvider.getTrans('app.capo_commessa') }}</th>
              <th>{{ TranslationProvider.getTrans('app.direttore_tecnico') }}</th>
            </tr>
            </thead>
            <tbody>
            <tr v-for="gec in gecContracts.items" :key="gec.id">
              <td>{{ gec['uuid'] }}</td>
              <td>{{ gec['object'] }}</td>
              <td>{{ getSubject(gec, 'main_contractor') }}</td>
              <td>{{ getSubject(gec, 'construction_site_manager') }}</td>
            </tr>
            </tbody>
          </table>

          <div
            v-if="gecContracts.lastPage > 1"
            class="d-flex justify-content-end m-2"
          >
            <Paginazione
              v-if="!networkError.enabled"
              :current-page="gecContracts.currentPage"
              :total-pages="gecContracts.lastPage"
              @page-changed="loadContracts"
            />
          </div>
        </div>
      </div>
    </div>

    <div class="d-flex justify-content-end mt-3">
      <button-torna-alla-lista
        class="me-auto"
        @click="
          router.back()
        "
      ></button-torna-alla-lista>

      <button
        type="button"
        class="btn btn-primary ms-2"
        @click="loadContracts(1, true)"
        :disabled="
          loadingGec ||
          !(
            dateTo &&
            dateFrom &&
            dateStart &&
            selectedWorkflow &&
            title &&
            selectedTemplate &&
            amount
          )
        "
      >
        <span v-if="!loadingGec"> Ricerca contratti </span>
        <span
          v-else-if="loadingGec"
          class="spinner-border spinner-border-sm"
          role="status"
          aria-hidden="true"
        >
        </span>
      </button>

      <button
        type="button"
        v-if="cId !== null"
        class="btn btn-primary ms-2"
        @click="updateCampaign"
        :disabled="creatingCampaignAndCompiledPolls"
      >
        <span> Salva campagna </span>
      </button>

      <button
        v-if="gecContracts"
        :disabled="loadingGec"
        @click="handleCreateCampaign(false)"
        class="btn btn-primary ms-2"
      >
        <span v-if="cId">Salva campagna e crea questionari mancanti</span>
        <span v-else>Crea campagna e questionari</span>
      </button>
    </div>

    <div
      class="modal"
      tabindex="-1"
      ref="modal-create-campaign"
      style="background-color: rgba(0, 0, 0, 0.8)"
    >
      <div class="modal-dialog modal-dialog-centered modal-lg">
        <div class="modal-content">
          <div class="modal-header">
            <h5 class="modal-title">
              <span v-if="cId">Salvataggio campagna e questionari</span>
              <span v-else>Creazione campagna e relativi questionari</span>
            </h5>
            <button
              v-if="!creatingCampaignAndCompiledPolls"
              type="button"
              class="btn-close"
              data-bs-dismiss="modal"
              aria-label="Chiudi"
              @click="
                () => {
                  modalCreateCampaign.classList.remove('d-block')
                }
              "
            >
            </button>
          </div>
          <div class="modal-body">
            <p v-if="cId">
              Continuando verrà salva la campagna corrente e verranno creati i
              questionari relativi ai contratti che non sono presenti,
              procedere?
            </p>
            <p v-else>
              Continuando verrà creata la campagna e i questionari
              relativi ai contratti, procedere?
            </p>
            <div
              class="alert alert-warning"
              v-if="creatingCampaignAndCompiledPolls"
            >
              Lasciare la pagina interromperà la creazione dei
              questionari mancanti
            </div>
            <div class="table-responsive"
                 v-if="creatingCampaignAndCompiledPolls || (!creatingCampaignAndCompiledPolls && creatingCampaignAndCompiledPollsError.enabled)">
              <table class="table table-sm">
                <thead>
                <tr>
                  <th scope="col">Questionari creati</th>
                  <th scope="col">Questionari saltati (già presenti)</th>
                  <th scope="col">Questionari non creati (con errori)</th>
                </tr>
                </thead>
                <tbody>
                <tr>
                  <td><span
                    class="badge rounded-pill text-bg-success">{{ creatingCampaignAndCompiledPollsNums.created }}</span>
                  </td>
                  <td><span
                    class="badge rounded-pill text-bg-light">{{ creatingCampaignAndCompiledPollsNums.skipped }}</span>
                  </td>
                  <td><span
                    class="badge rounded-pill text-bg-danger">{{ creatingCampaignAndCompiledPollsNums.errors }}</span>
                  </td>
                </tr>
                </tbody>
              </table>
            </div>
            <div
              v-if="creatingCampaignAndCompiledPollsError.enabled"
              class="alert alert-danger mt-2"
              role="alert"
            >
              {{ creatingCampaignAndCompiledPollsError.message }}
            </div>
            <div
              v-if="creatingCampaignAndCompiledPolls || (!creatingCampaignAndCompiledPolls && creatingCampaignAndCompiledPollsError.enabled)"
              class="progress"
              role="progressbar"
              aria-label="Progresso importazione"
              aria-valuenow="75"
              aria-valuemin="0"
              aria-valuemax="100"
            >
              <div
                class="progress-bar progress-bar-striped progress-bar-animated bg-success"
                :style="'width: ' + creatingCampaignAndCompiledPollsPerc + '%'"
              >
                {{ creatingCampaignAndCompiledPollsPerc }}%
              </div>
            </div>
          </div>
          <div class="modal-footer">
            <button
              type="button"
              class="btn btn-secondary"
              data-bs-dismiss="modal"
              v-if="!creatingCampaignAndCompiledPolls"
              @click="
                () => {
                  modalCreateCampaign.classList.remove('d-block')
                }
              "
            >
              Chiudi
            </button>
            <button
              type="button"
              class="btn btn-primary"
              v-if="!creatingCampaignAndCompiledPollsError.enabled"
              :disabled="creatingCampaignAndCompiledPolls"
              @click="handleCreateCampaign(true)"
            >
              <span v-if="!creatingCampaignAndCompiledPolls"> Continua </span>
              <span
                v-else-if="creatingCampaignAndCompiledPolls"
                class="spinner-border spinner-border-sm"
                role="status"
                aria-hidden="true"
              >
              </span>
            </button>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<style scoped lang="scss">
.fa-trash {
  width: 0.9rem;
}

.vs__dropdown-toggle {
  font-size: 1rem;
  padding: 0.25rem 0.10rem !important;
}
</style>
